/**
 * ImpactMetrics Component
 * TASK-396: Interactive Proposal Builder
 *
 * Displays before/after impact metrics for a proposal.
 * Color-coded: green = improved, red = worsened.
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface MetricDelta {
  label: string;
  before: number;
  after: number;
  unit: string;
  invertColor?: boolean; // true = lower is better (e.g., risk)
}

interface ImpactMetricsProps {
  metrics: MetricDelta[];
}

// ============================================================================
// Component
// ============================================================================

const ImpactMetrics: React.FC<ImpactMetricsProps> = ({ metrics }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {metrics.map((m) => {
        const delta = m.after - m.before;
        const improved = m.invertColor ? delta < 0 : delta > 0;
        const worsened = m.invertColor ? delta > 0 : delta < 0;
        const neutral = Math.abs(delta) < 0.01;

        return (
          <div key={m.label} className="bg-gray-50 rounded-lg px-3 py-2.5">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">
              {m.label}
            </p>
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-gray-800">
                {m.after.toFixed(1)}{m.unit}
              </span>
              {!neutral && (
                <span className={`flex items-center gap-0.5 text-xs font-medium ${
                  improved ? 'text-green-600' : worsened ? 'text-red-600' : 'text-gray-400'
                }`}>
                  {improved ? <TrendingUp className="w-3 h-3" /> : worsened ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                  {delta > 0 ? '+' : ''}{delta.toFixed(1)}{m.unit}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ImpactMetrics;
